import * as React from 'react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cva } from 'class-variance-authority';
import { cn } from '../../lib/utils';

/**
 * Radio component with exact Hyperkit styling
 *
 * Hyperkit Specs (Node ID: 177:1406):
 * - Size: 16x16px circle in 24x24px container
 * - Border Radius: 99px (full circle)
 * - Unselected: White bg, subtle shadow border
 * - Selected: Grey-950 bg (#09090B), white 8px dot
 * - Focus: 4px ring at 15% opacity
 */
const radioVariants = cva(
  'aspect-square h-4 w-4 rounded-full border transition-all duration-100 focus:outline-none focus-visible:ring-4 disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'border-grey-200 bg-white shadow-[0px_1px_1px_0px_rgba(18,18,18,0.1),0px_0px_0px_1px_rgba(18,18,18,0.1)] hover:border-grey-300 hover:shadow-[0px_1px_1px_0px_rgba(18,18,18,0.1),0px_0px_0px_1px_rgba(18,18,18,0.15),0px_1px_3px_0px_rgba(18,18,18,0.1)] focus-visible:ring-grey-950/15 data-[state=checked]:bg-grey-950 data-[state=checked]:border-grey-950 data-[state=checked]:shadow-[0px_0px_0px_1px_#121212] dark:border-grey-700 dark:bg-grey-900 dark:data-[state=checked]:bg-grey-50 dark:data-[state=checked]:border-grey-50',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

const RadioGroup = React.forwardRef(({ className, ...props }, ref) => (
  <RadioGroupPrimitive.Root
    className={cn('grid gap-3', className)}
    {...props}
    ref={ref}
  />
));
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;

const RadioGroupItem = React.forwardRef(({ className, variant, ...props }, ref) => (
  <RadioGroupPrimitive.Item
    ref={ref}
    className={cn(radioVariants({ variant }), className)}
    {...props}
  >
    <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
      <div className="h-2 w-2 rounded-full bg-white shadow-[0px_1px_1px_0px_rgba(18,18,18,0.1)] dark:bg-grey-950" />
    </RadioGroupPrimitive.Indicator>
  </RadioGroupPrimitive.Item>
));
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;

/**
 * RadioField - Radio with label and optional description
 */
const RadioField = React.forwardRef(
  ({ className, label, description, value, ...props }, ref) => {
    const id = React.useId();
    return (
      <div className={cn('flex items-start gap-3', className)}>
        <RadioGroupItem ref={ref} value={value} id={id} {...props} />
        <div className="flex flex-col gap-0.5">
          <label
            htmlFor={id}
            className="text-sm font-medium leading-5 text-grey-950 cursor-pointer dark:text-grey-50"
          >
            {label}
          </label>
          {description && (
            <p className="text-xs text-grey-500 leading-4">{description}</p>
          )}
        </div>
      </div>
    );
  }
);
RadioField.displayName = 'RadioField';

export { RadioGroup, RadioGroupItem, RadioField, radioVariants };
